import { Helper, ProductPillar, ProductType, RangeType, RetailChannel } from '@app/shared/utils';
import { createSelector } from '@ngrx/store';
import { AppState } from '..';
import { ProductsState } from '@app/store/products/products.state';
import { ProductCategoriesFilters } from '@app/core/models/territories-filters';
import { TerritoryType } from '@app/shared/utils/enums/territoryType';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { territories } from '@app/shared/data/territories-filters-content';
import { rangeCategory } from '@app/shared/data/range-filters-content';
import { SelectedProduct } from '@app/core/models';

const productsState = (state: AppState) => state.products;

export const selectAllProductsSuccess = createSelector(productsState, (state) => state.allProductList);

const selectSelectedProductsAndPacks = createSelector(productsState, (state: ProductsState) => state?.selectedProducts);

export const selectSelectedProducts = createSelector(selectSelectedProductsAndPacks, (selectedProducts: SelectedProduct[]) =>
  selectedProducts?.map((selectedProduct) => selectedProduct?.product),
);

export const selectSelectedPackIds = createSelector(selectSelectedProductsAndPacks, (selectedProducts: SelectedProduct[]) =>
  selectedProducts?.map((item) => item.packId).filter((item) => !!item),
);

export const selectHasDryProduct = createSelector(selectAllProductsSuccess, (allProducts) =>
  allProducts.some((product) => product.productType === ProductType.Dry),
);
export const selectHasWetProduct = createSelector(selectAllProductsSuccess, (allProducts) =>
  allProducts.some((product) => product.productType === ProductType.Wet),
);

export const selectHasTreatsProduct = createSelector(selectAllProductsSuccess, (allProducts) =>
  allProducts.some((product) => product.productType === ProductType.Treats),
);

export const selectSelectedDryProduct = createSelector(
  selectSelectedProductsAndPacks,
  (selectedProducts: SelectedProduct[]) => selectedProducts?.find((product) => product?.product?.productType === ProductType.Dry)?.product,
);

export const selectSelectedWetProduct = createSelector(
  selectSelectedProductsAndPacks,
  (selectedProducts: SelectedProduct[]) => selectedProducts?.find((product) => product?.product?.productType === ProductType.Wet)?.product,
);

export const selectSelectedTreatsProduct = createSelector(
  selectSelectedProductsAndPacks,
  (selectedProducts: SelectedProduct[]) =>
    selectedProducts?.find((product) => product?.product?.productType === ProductType.Treats)?.product,
);

const selectSelectedDryPackId = createSelector(
  selectSelectedProductsAndPacks,
  (selectedProducts: SelectedProduct[]) =>
    selectedProducts?.find((selectedProduct) => selectedProduct?.product?.productType === ProductType.Dry)?.packId,
);

const selectSelectedWetPackId = createSelector(
  selectSelectedProductsAndPacks,
  (selectedProducts: SelectedProduct[]) =>
    selectedProducts?.find((selectedProduct) => selectedProduct?.product?.productType === ProductType.Wet)?.packId,
);

const selectSelectedTreatsPackId = createSelector(
  selectSelectedProductsAndPacks,
  (selectedProducts: SelectedProduct[]) =>
    selectedProducts?.find((selectedProduct) => selectedProduct?.product?.productType === ProductType.Treats)?.packId,
);

export const selectSelectedDryPack = createSelector(selectSelectedDryProduct, selectSelectedDryPackId, (product, packId) =>
  product?.packages?.find((item) => item.sCode === packId),
);

export const selectSelectedWetPack = createSelector(selectSelectedWetProduct, selectSelectedWetPackId, (product, packId) =>
  product?.packages?.find((item) => item.sCode === packId),
);

export const selectSelectedTreatsPack = createSelector(selectSelectedTreatsProduct, selectSelectedTreatsPackId, (product, packId) =>
  product?.packages?.find((item) => item.sCode === packId),
);

export const selectPriceMargin = createSelector(productsState, (state) => state.priceMargin);

export const selectPriceListToUpdate = createSelector(productsState, (state) => state.priceListToUpdate);

export const selectPriceListUpdateLoading = createSelector(productsState, (state) => state.priceListUpdateLoading);

export const selectShowPriceListActions = createSelector(productsState, (state) => state.showPriceListActions);

export const selectProductsLoading = createSelector(productsState, (state) => state.productsLoading);

export const selectGetAllProductsFail = createSelector(productsState, (state) => state.getAllProductsFail);

export const selectFilteredProducts = createSelector(productsState, (state) => state.filteredProductList);

const selectFilteredProductsFlatten = createSelector(selectFilteredProducts, (filteredProductList) =>
  Object.values(filteredProductList).reduce((a, b) => [...a, ...b], []),
);

export const selectFilteredCAProductsFlatten = createSelector(selectFilteredProductsFlatten, (products) => {
  return products.filter((product) => product.clinicalAlliance);
});

export const selectFilteredProductsLength = createSelector(productsState, (state) => state.filteredProductLength);

export const selectFiltersValues = createSelector(productsState, (state) => state.filtersValues);

export const selectSelectedCategories = createSelector(productsState, (state) => state.selectedCategories);

export const selectMufProductsBySpecie = createSelector(selectAllProductsSuccess, selectFiltersValues, (allProducts, filtersValues) =>
  allProducts.filter((product) => product.retailChannel === RetailChannel.MULTIFUNCTION && product.speciesCode === filtersValues.specie),
);

export const selectSpecificSpecieCode = createSelector(productsState, (state) => state.specificSpecieCode);

export const selectSpecificLifestageType = createSelector(productsState, (state) => state.specificLifestageType);

const selectCurrentPillar = createSelector(productsState, (state) => state.filtersValues?.pillar);

export const selectProductBookCategories = (isSolProductsInVetEnable: boolean) =>
  createSelector(selectFilteredProducts, selectCurrentPillar, (products, pillar) => {
    const unsortedCategories =
      pillar === ProductPillar.VET
        ? getSortedCategories(territories, [TerritoryType.BirthAndGrowth])
        : getSortedCategories(rangeCategory, [TerritoryType.BirthAndGrowth]);
    const filteredUnsortedCategories = unsortedCategories
      .filter((cat) => products && Object.keys(products).includes(cat.code) && products[cat.code].length)
      .filter((cat) => (pillar === ProductPillar.VET && !isSolProductsInVetEnable ? cat.code !== TerritoryType.BirthAndGrowth : true));
    if (filteredUnsortedCategories.length) {
      return filteredUnsortedCategories;
    }
    return unsortedCategories.map((category) => ({
      ...category,
      disabled: true,
    }));
  });

function getSortedCategories(
  categories: ProductCategoriesFilters[],
  firstDisplayedCategories?: (TerritoryType | RangeType)[],
): ProductCategoriesFilters[] {
  const firstCategories = categories
    .filter((item) => firstDisplayedCategories?.includes(item.code))
    .sort((a, b) => Helper.alphabeticalOrder(translateKey(a.translationCode), translateKey(b.translationCode)));
  const otherCategories = categories
    .filter((item) => !firstDisplayedCategories?.includes(item.code))
    .sort((a, b) => Helper.alphabeticalOrder(translateKey(a.translationCode), translateKey(b.translationCode)));

  return [...firstCategories, ...otherCategories];
}
